import Badge, { type BadgeTone } from '@/components/base/Badge';
import { railsByMarket } from '@/utils/payout';
import type { RailHealth, RailStatus } from '@/types/portal';

interface RailStatusStripProps {
  rails: RailStatus[];
}

const healthMeta: Record<RailHealth, { tone: BadgeTone; icon: string }> = {
  Operational: { tone: 'accent', icon: 'ri-checkbox-circle-line' },
  Degraded: { tone: 'secondary', icon: 'ri-alert-line' },
  Down: { tone: 'primary', icon: 'ri-close-circle-line' },
};

export default function RailStatusStrip({ rails }: RailStatusStripProps) {
  const markets = railsByMarket(rails);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Payout rail status
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            Live health per market · success rate over the last 24 hours
          </p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-base text-secondary-800">
          <i className="ri-signal-tower-line" />
        </span>
      </header>

      {markets.length === 0 ? (
        <p className="px-5 py-4 text-xs text-foreground-500">No rail health available yet.</p>
      ) : null}
      <div className="grid grid-cols-1 divide-y divide-background-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {markets.map((market) => (
          <div key={market.market} className="px-5 py-4">
            <div className="flex items-center justify-between">
              <p className="font-heading text-sm font-semibold text-foreground-950">
                {market.market}
              </p>
              <Badge tone={healthMeta[market.worstHealth].tone} icon={healthMeta[market.worstHealth].icon}>
                {market.worstHealth}
              </Badge>
            </div>
            <ul className="mt-3 space-y-2.5">
              {market.rails.map((rail) => (
                <li key={rail.id} className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex min-w-0 items-center gap-2">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-50 text-xs text-foreground-600">
                        <i className={rail.icon} />
                      </span>
                      <span className="truncate text-sm text-foreground-800">{rail.rail}</span>
                    </span>
                    <span className="shrink-0 text-xs font-medium text-foreground-600">
                      {rail.currency}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-pulse-line" />
                      {rail.successRate}% success
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line" />
                      {rail.avgSettlement}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-loader-4-line" />
                      {rail.inFlight} in flight
                    </span>
                  </div>
                  {rail.note ? (
                    <p className="mt-1 text-[11px] text-foreground-500">{rail.note}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}